import type { SanityCardButton } from '#/lib/sanity/queries/buttonRow';
import { cn } from '#/lib/utils';
import { Button } from '#/components/Button';

type Theme = 'light' | 'dark';

// Card button styles map onto the shared Button variants. The card "arrow"
// style is just the `link` variant (which renders the inline 12px arrow).
const VARIANT_BY_STYLE = {
  primary: 'primary',
  outline: 'secondary',
  arrow: 'link',
} as const;

// A single button uses its chosen style. With two buttons the row is always
// Primary (first) then Outline (second).
function resolveStyle(
  button: SanityCardButton,
  index: number,
  total: number,
): SanityCardButton['style'] {
  if (total <= 1) return button.style;
  return index === 0 ? 'primary' : 'outline';
}

/**
 * Shared button row used by the Card Grid (and reusable elsewhere). Renders up
 * to two buttons through the design-system `Button` (theme-aware).
 *
 * Layout keys off the *card's own width* via container queries (the card is a
 * `@container`), with an absolute-px threshold (`@[430px]`) because the root
 * font-size is 10px:
 * - Below 430px of card width: buttons stack full width.
 * - At/above it: a single button hugs its label; two buttons share an equal
 *   two-column row.
 *
 * `restackAtM` is set for 3/4-column sections — once the grid is genuinely
 * multi-column (viewport ≥ `m`) those narrow cards should always stack, so the
 * `m:` overrides win via `!` regardless of stylesheet order.
 */
export function ButtonRow({
  buttons,
  theme,
  restackAtM,
}: {
  buttons: SanityCardButton[];
  theme: Theme;
  restackAtM?: boolean;
}) {
  const valid = buttons.filter((button) => button.href);
  if (!valid.length) return null;

  const count = valid.length;
  const layout: 'grid' | 'hug' = count > 1 ? 'grid' : 'hug';

  const widthClass = cn(
    layout === 'hug' ? 'w-full @[430px]:w-max' : 'w-full',
    layout === 'hug' && restackAtM && 'm:!w-full',
  );

  return (
    <div
      className={cn(
        'mt-auto pt-8',
        layout === 'hug' &&
          'flex flex-col gap-12 @[430px]:flex-row @[430px]:items-center',
        layout === 'hug' && restackAtM && 'm:!flex-col m:!items-stretch',
        layout === 'grid' &&
          'flex flex-col gap-12 @[430px]:inline-grid @[430px]:grid-flow-col @[430px]:auto-cols-[1fr] @[430px]:self-start',
        layout === 'grid' &&
          restackAtM &&
          'm:!flex m:!flex-col m:!self-stretch',
      )}
    >
      {valid.map((button, index) => {
        const style = resolveStyle(button, index, count);
        const isArrow = style === 'arrow';
        // The arrow (link) variant is always solo and hugs its label, so it
        // skips both the width class and the cross-card equalization.
        const equalize = !isArrow && layout === 'hug';

        return (
          <Button
            key={button._key ?? index}
            href={button.href}
            target={button.target}
            variant={VARIANT_BY_STYLE[style]}
            theme={theme}
            className={isArrow ? undefined : cn(widthClass, 'text-center')}
            data-equal-cta={equalize ? '' : undefined}
          >
            {button.title}
          </Button>
        );
      })}
    </div>
  );
}
